import { useCallback, useMemo, useReducer, useRef, useSyncExternalStore } from "react";
import { isProduction } from "@web-monorepo/environment";
import { captureException } from "@web-monorepo/telemetry";
import { StorageType } from "./types";
import { sessionStorage } from "./sessionStorage";
import { localStorage } from "./localStorage";
import { getVersion, subscribe as subscribeToStorage } from "./storageSubscriptions";

export const useStorage = <T>(type: StorageType, key: string, defaultValue: T, enabled: boolean = true) => {
  const storage = type === "session" ? sessionStorage : localStorage;

  // Store defaultValue in a ref to avoid infinite loops when consumers pass
  // unstable references (e.g., `[]` or `{}` literals).
  const defaultValueRef = useRef(defaultValue);

  // Cache parsed value by raw string to provide referential stability for useSyncExternalStore
  const cacheRef = useRef<{ raw: string | null; parsed: T } | null>(null);

  const getSnapshot = useCallback((): T => {
    try {
      const item = storage.getItem(key);

      if (cacheRef.current !== null && cacheRef.current.raw === item) {
        return cacheRef.current.parsed;
      }

      const parsed = item != null ? (JSON.parse(item) as T) : defaultValueRef.current;
      cacheRef.current = { raw: item, parsed };
      return parsed;
    } catch (error) {
      if (isProduction) {
        captureException(error, { extra: { key, type } });
        return defaultValueRef.current;
      }
      throw error;
    }
  }, [key, storage, type]);

  const subscribeToChanges = useCallback(
    (callback: () => void) => subscribeToStorage(type, key, callback),
    [type, key],
  );

  const storedValue = useSyncExternalStore(subscribeToChanges, getSnapshot, getSnapshot);

  // Workaround for React 18.2's useSyncExternalStore "dropped update" bug
  // (https://github.com/facebook/react/issues/25565, fixed by
  // https://github.com/facebook/react/pull/25578 but not in 18.2.x): when the
  // consuming component dispatches state during the render phase — e.g. via
  // useWatch / setVisibleCount in the admin Feature Switches list — the
  // hook's passive effect that refreshes the cached store instance is
  // dropped, so a subsequent mutation back to the prior value fires subscribe
  // but no re-render is scheduled. Dispatching a normal state update here
  // forces a reliable re-render regardless.
  const [, forceLocalUpdate] = useReducer((n: number) => n + 1, 0);

  const setValue = useCallback(
    (value: T | ((arg: T) => T)) => {
      if (!enabled) {
        return;
      }

      // Write to storage before notifying so data persists even if component unmounts
      const currentValue = getSnapshot();
      const valueToStore = value instanceof Function ? value(currentValue) : value;

      try {
        storage.setItem(key, JSON.stringify(valueToStore));
        // eslint-disable-next-line no-catch-all/no-catch-all
      } catch (error) {
        // Don't notify subscribers since storage write failed
        captureException(error, { extra: { key, type } });
        return;
      }

      forceLocalUpdate();
    },
    [storage, enabled, getSnapshot, key, type],
  );

  return enabled ? ([storedValue, setValue] as const) : ([defaultValueRef.current, setValue] as const);
};

export function useSessionStorage<T>(key: string, defaultValue: T) {
  return useStorage("session", key, defaultValue);
}

export function useLocalStorage<T>(key: string, defaultValue: T) {
  return useStorage("local", key, defaultValue);
}

export function useStorageSubscriptionVersion(
  type: StorageType,
  keyOrKeys: string | readonly string[],
  enabled: boolean = true,
): number {
  const keys = useMemo<readonly string[]>(
    () => (typeof keyOrKeys === "string" ? [keyOrKeys] : [...keyOrKeys]),
    [keyOrKeys],
  );

  const subscribeToChanges = useCallback(
    (callback: () => void) => {
      if (!enabled) {
        return () => {};
      }

      const unsubs: Array<() => void> = [];
      for (const key of keys) {
        unsubs.push(subscribeToStorage(type, key, callback));
      }
      return () => {
        for (const unsub of unsubs) {
          unsub();
        }
      };
    },
    [enabled, keys, type],
  );

  const getSnapshot = useCallback(() => {
    if (!enabled) {
      return 0;
    }

    let totalVersion = 0;
    for (const key of keys) {
      totalVersion += getVersion(type, key);
    }
    return totalVersion;
  }, [enabled, keys, type]);

  return useSyncExternalStore(subscribeToChanges, getSnapshot, getSnapshot);
}

export { subscribe, notify, notifyAll } from "./storageSubscriptions";
